// Step 1 — Load Takeoff: show the job's attached file, run the fake parse job.
import { AlertTriangle, ArrowRight, CheckCircle2, FileSpreadsheet, RefreshCw, ScanLine } from "lucide-react";
import { useAlert } from "context/AlertContext";
import useBackgroundJob from "../../hooks/useBackgroundJob";
import { PARSE_STAGES, startParseTakeoff } from "../../mock/estimateService.mock";
import { Button, Card, CardHeader } from "../../components/ui";
import { JobProgress } from "../../components/PageStates";
import { fmtDateTime, fmtInt } from "../../utils/format";

function FileMeta({ label, value }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-text_muted">{label}</dt>
      <dd className="truncate text-[13px] text-text_primary">{value}</dd>
    </div>
  );
}

export default function StepLoadTakeoff({ job, parse, onParsed, onContinue }) {
  const { showAlert } = useAlert();
  const bg = useBackgroundJob();
  const file = job.takeoffFile;

  const handleParse = async () => {
    try {
      const result = await bg.run(() => startParseTakeoff(job.jobId));
      if (result) {
        onParsed(result);
        showAlert({ type: "success", message: `${result.stats.lineCount} takeoff lines parsed` });
      }
    } catch (e) {
      showAlert({ type: "error", message: e.message || "Could not parse the takeoff file" });
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <Card>
        <CardHeader icon={FileSpreadsheet} title="Takeoff file" subtitle="Attached to this job by the client" />
        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-center gap-3 rounded-[4px] border border-border bg-surface_alt p-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[4px] bg-success_bg text-success">
              <FileSpreadsheet size={20} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold text-text_primary" title={file.name}>
                {file.name}
              </p>
              <p className="text-[11.5px] text-text_muted">{fmtInt(file.sizeKb)} KB · Excel workbook</p>
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-3">
            <FileMeta label="Uploaded by" value={file.uploadedBy} />
            <FileMeta label="Uploaded" value={fmtDateTime(file.uploadedAt)} />
          </dl>

          {bg.running ? (
            <div className="rounded-[4px] border border-border p-3">
              <JobProgress progress={bg.progress} stages={PARSE_STAGES} title="Parsing takeoff…" />
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant={parse ? "secondary" : "primary"} size="md" icon={parse ? RefreshCw : ScanLine} onClick={handleParse}>
                {parse ? "Re-parse file" : "Load & Parse"}
              </Button>
              {parse && <span className="text-[11.5px] text-text_muted">Re-parsing resets your surface mappings.</span>}
            </div>
          )}
          {bg.error && !bg.running && (
            <p className="text-[12px] text-danger" role="alert">
              {bg.error.message}
            </p>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader icon={ScanLine} title="Parse result" subtitle="Lines, units and suggested surfaces" />
        {parse ? (
          <div className="flex flex-col gap-4 p-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={22} className="flex-shrink-0 text-success" aria-hidden="true" />
              <div>
                <p className="text-[15px] font-semibold text-text_primary">
                  {parse.stats.lineCount} lines found
                </p>
                <p className="text-[12px] text-text_muted">
                  across {parse.stats.sectionCount} sections ·{" "}
                  {parse.lines.filter((l) => l.suggestedSurfaceId).length} with a suggested surface
                </p>
              </div>
            </div>
            {parse.stats.warnings.length > 0 && (
              <div className="rounded-[4px] border border-warning_border bg-warning_bg p-3">
                <p className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-warning">
                  <AlertTriangle size={14} aria-hidden="true" />
                  {parse.stats.warnings.length} warning{parse.stats.warnings.length > 1 ? "s" : ""}
                </p>
                <ul className="list-disc pl-5 text-[12px] text-warning">
                  {parse.stats.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <Button variant="primary" size="md" iconRight={ArrowRight} onClick={onContinue}>
                Continue to Map Surfaces
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex min-h-[180px] items-center justify-center p-6 text-center text-[12.5px] text-text_muted">
            Run “Load & Parse” to extract the takeoff lines.
          </div>
        )}
      </Card>
    </div>
  );
}
